import { useEffect, useRef } from "react";

import { clearUserActionTriggerIntent, consumeUserActionTriggerIntent, getMessageBus } from "@jti/fe-message-bus";
import { addTrackingEventListener, TrackingEvents } from "@jti/tracker";

import type { UserActionTrigger } from "../types";

const USER_ACTION_TO_TRACKING_EVENT: Partial<Record<UserActionTrigger, TrackingEvents>> = {
  addToCart: TrackingEvents.TrackingCartQuantityAdd,
  removeFromCart: TrackingEvents.TrackingCartQuantityRemove,
  placeOrder: TrackingEvents.TrackingOrderSummaryPurchase,
  completeRegistration: TrackingEvents.TrackingSignUpRegular,
  loginSuccess: TrackingEvents.TrackingLoginSuccess,
};

// Triggers that fire on a page the user is about to leave (auth -> profile redirect).
// For these we DO NOT attach a live DOM listener, because the listener would open the
// modal on the auth page during the brief window before the redirect tears it down.
// Instead we rely on a persisted or sticky intent consumed on the destination page.
const POST_AUTH_TRIGGERS = new Set<UserActionTrigger>(["completeRegistration", "loginSuccess"]);

function isPostAuthTrigger(
  trigger: UserActionTrigger
): trigger is Extract<UserActionTrigger, "completeRegistration" | "loginSuccess"> {
  return POST_AUTH_TRIGGERS.has(trigger);
}

export function useUserActionTrigger(
  onTrigger: (trigger?: UserActionTrigger) => void,
  userActionTrigger: UserActionTrigger | undefined,
  isEnabled: boolean | undefined,
  isModalOpened: boolean
) {
  // Keep a ref so the deferred sticky-intent timer always calls the latest onTrigger,
  // even if sibling hooks (e.g. usePurchasedXDaysAgo) caused a re-render before the
  // timer fires and produced a newer version of the callback.
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  });

  // On mount: consume same-page sticky intent first; this keeps non-reload auth flows working.
  useEffect(() => {
    if (!isEnabled || !userActionTrigger || isModalOpened) return;

    const messageBus = getMessageBus();
    const stickyIntent = messageBus.getLastHistory("user.action.trigger");

    if (stickyIntent?.payload?.trigger === userActionTrigger) {
      // Clear immediately so a concurrent second consumer can't double-fire.
      messageBus.removeHistoryItem("user.action.trigger");
      clearUserActionTriggerIntent();

      // Defer to the next tick so sibling hooks (usePurchasedXDaysAgo, useWelcomeQueue
      // sticky replay, etc.) finish their first effect pass and reach a steady state
      // before openModal evaluates its guards. Use the ref so we always call the
      // latest version of onTrigger even if a re-render occurred between effect and timer.
      const timeoutId = setTimeout(() => {
        onTriggerRef.current(userActionTrigger);
      }, 0);

      return () => clearTimeout(timeoutId);
    }

    if (!isPostAuthTrigger(userActionTrigger)) return;

    const consumedIntent = consumeUserActionTriggerIntent(userActionTrigger);
    if (!consumedIntent) return;

    messageBus.removeHistoryItem("user.action.trigger");

    const timeoutId = setTimeout(() => {
      onTriggerRef.current(consumedIntent.trigger);
    }, 0);

    return () => clearTimeout(timeoutId);
    // Re-run when trigger config changes; intentionally uses onTriggerRef for callback freshness.
  }, [isEnabled, userActionTrigger, isModalOpened]);

  // Live DOM listener for same-page actions only (addToCart, removeFromCart, placeOrder).
  // Post-auth triggers are intentionally excluded — see POST_AUTH_TRIGGERS above.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    if (!userActionTrigger) {
      return;
    }
    if (isModalOpened) {
      return;
    }
    if (isPostAuthTrigger(userActionTrigger)) {
      return;
    }

    const eventName = USER_ACTION_TO_TRACKING_EVENT[userActionTrigger];
    if (!eventName) {
      console.warn("[OnsitePersonalization][useUserActionTrigger] unknown trigger:", userActionTrigger);
      return;
    }

    const handler = () => {
      onTrigger(userActionTrigger);
    };
    addTrackingEventListener(eventName, handler);

    return () => {
      document.removeEventListener(eventName, handler);
    };
  }, [isEnabled, userActionTrigger, isModalOpened, onTrigger]);
}
